import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getGoalProfile, upsertGoalProfile, getGoalHistory } from '@/services/goalProfileService';
import { useAuth } from '@/hooks/use-auth-compat';
import type { GoalProfileRequest, GoalProfileResponse } from '@/types/goalProfile';
import type { GoalHistoryEntry } from '@/types/registeredStudent';

/** Reads the student's saved goal profile. Returns null (not an error) when none exists yet. */
export function useGoalProfile() {
  const { user } = useAuth();
  const token = user?.access_token ?? '';
  return useQuery<GoalProfileResponse | null>({
    queryKey: ['goalProfile'],
    enabled: !!token,
    queryFn: async () => {
      try {
        return await getGoalProfile(token);
      } catch (e) {
        if (e instanceof Error && e.message === 'GOAL_PROFILE_NOT_FOUND') return null;
        throw e;
      }
    },
  });
}

export function useUpsertGoalProfile() {
  const { user } = useAuth();
  const token = user?.access_token ?? '';
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: GoalProfileRequest) => upsertGoalProfile(token, data),
    onSuccess: (res) => {
      qc.setQueryData(['goalProfile'], res);
      void qc.invalidateQueries({ queryKey: ['goalHistory'] });
      // Saving the goal profile flips the user's goalOnboardingCompleted flag
      // server-side — refresh the cached /me so gates (e.g. StudentDashboard)
      // stop redirecting back to onboarding.
      void qc.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });
}

export function useGoalHistory() {
  const { user } = useAuth();
  const token = user?.access_token ?? '';
  return useQuery<GoalHistoryEntry[]>({
    queryKey: ['goalHistory'],
    enabled: !!token,
    queryFn: () => getGoalHistory(token),
  });
}
